import { ChangeDetectorRef, Component, DestroyRef, effect, ElementRef, inject, input, model, output, signal, viewChildren } from '@angular/core';
import { ApiSettings, PaginationConstants } from '../../core/constants/constants';
import { InViewPortDirective } from '../../core/directives/in-view-port.directive';
import { PaginationUtilities } from '../../core/utilities/pagination.utilities';
import { ListLayouts } from '../../core/enums/base.enums';

@Component({
  selector: '[ms-pagination]',
  imports: [InViewPortDirective],
  templateUrl: './pagination.component.html'
})
export class PaginationComponent {

  // Bootstrap's placeholder class, applied to a cell's content while its row is still a skeleton.
  private static readonly SKELETON_MARKER_SELECTOR = '.placeholder';
  private static readonly TABLE_SELECTOR = 'table';
  private static readonly TABLE_ROW_SELECTOR = 'tbody tr';

  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  private scrollHandler: (() => void) | null = null;
  private scrollContainer: Element | Window | null = null;
  private rafId: number | null = null;
  private isListening = false;
  private fillTimeoutId: number | null = null;

  // The desktop (table row) and mobile sentinels this component renders when there are more results.
  private sentinels = viewChildren(InViewPortDirective);

  public initalized = false;
  public skip = model(0);
  public take = input(0);
  public hasNext = input(false);
  public itemCount = input(0);
  public hidden = input(false);
  public scrollToElementName = input<string>(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME());
  public scrollToElementMobileName = input<string>(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME());

  public showJumpToTop = signal(false);

  public loadMoreInView = output<void>();
  public jumpToClicked = output<string>();

  constructor() {
    effect(() => {
      const hasNext = this.hasNext();
      const itemCount = this.itemCount();
      const skip = this.skip();
      const hidden = this.hidden();

      if (!hasNext && itemCount > 0 && skip > 0 && !hidden) {
        this.startScrollTracking();
      } else {
        this.stopScrollTracking();
      }
    });

    // Re-runs every time a page of results finishes rendering (itemCount changes) or the list learns
    // there is more to fetch (hasNext flips) — the two moments at which the loaded rows might have
    // grown enough to push the sentinel off screen.
    effect(() => {
      const hasNext = this.hasNext();
      const itemCount = this.itemCount();
      const hidden = this.hidden();

      this.reportRenderedRowHeight();
      this.scheduleViewportFill(hasNext, itemCount, hidden);
    });

    this.destroyRef.onDestroy(() => {
      this.stopScrollTracking();
      this.cancelViewportFill();
    });
  }

  public getNextSkip(): number {
    if (!this.take()) {
      return this.skip() + ApiSettings.DEFAULT_TAKE();
    } else {
      return this.skip() + this.take();
    }
  }

  public jumpTo(elementId: string) {
    this.jumpToClicked.emit(elementId);
  }

  public inViewPort() {
    // Guard against advancing skip when there is nothing left to load. If the sentinel becomes
    // visible during a parent-driven reset (skip goes to 0 while hasNext is still stale-false),
    // an unguarded increment silently pushes skip past what was actually loaded and causes the
    // next legitimate load-more to fetch an empty page — surfacing as premature "No more results".
    if (!this.hasNext()) return;
    this.skip.set(this.getNextSkip());
    this.loadMoreInView.emit();
  }

  //#region Private Methods

  /**
   * Infinite scroll is driven by an IntersectionObserver on the sentinel rendered at the bottom of
   * this component, and an observer only reports *transitions* in and out of view. When the first
   * page of results is too short to fill the scroll container — a tall/vertical monitor, or simply a
   * large one — the sentinel is on screen from the moment it renders and never leaves, so no further
   * transition is ever reported: the list stalls at one page with no scrollbar to pull the next one
   * in. (Nudging the window resizes the container, which perturbs the geometry enough to produce a
   * fresh intersection record — that's why resizing the browser "fixes" it.) The very first
   * intersection is worse than useless too: it fires while the initial request is still in flight,
   * so inViewPort() discards it because hasNext is still false, spending the one event we get.
   *
   * So instead of waiting for a transition that will never come, re-read the sentinel's current
   * visibility after each page renders and keep asking for more until it is pushed off screen and
   * the observer can take over.
   */
  private scheduleViewportFill(hasNext: boolean, itemCount: number, hidden: boolean): void {
    this.cancelViewportFill();

    if (hidden || !hasNext || itemCount === 0) return;

    // Wait out the owning list's load-more cooldown before asking for another page. That cooldown
    // silently drops requests made while it is closed, but inViewPort() advances skip regardless —
    // so an early request would cost a whole page of results rather than just being ignored.
    this.fillTimeoutId = window.setTimeout(() => {
      this.fillTimeoutId = null;

      if (this.isSentinelInView()) {
        this.inViewPort();
      }
    }, PaginationConstants.LOAD_MORE_DEBOUNCE_TIME_MILLISECONDS());
  }

  private cancelViewportFill(): void {
    if (this.fillTimeoutId !== null) {
      clearTimeout(this.fillTimeoutId);
      this.fillTimeoutId = null;
    }
  }

  /**
   * A sentinel that is still on screen after a page rendered means the loaded rows did not reach the
   * bottom of the scroll container, so there is room for more. Asking the sentinel rather than
   * measuring the container keeps this correct in the layouts that would otherwise mislead us:
   * lists render a desktop table and a mobile card stack side by side and hide the inapplicable one,
   * and audit logs live inside collapsible panels — in both cases the hidden sentinel reports itself
   * out of view, so only the pagination the user can actually see requests pages.
   */
  private isSentinelInView(): boolean {
    return this.sentinels().some(sentinel => sentinel.isInViewPort());
  }

  /**
   * Records how tall this list's rows actually turned out, so its next load can size its first page
   * to fill the viewport rather than guess at it. Runs on every rendered page: rows are only ever
   * measured from a layout the browser has already performed, so nothing here depends on an assumed
   * height that could drift out of date as the CSS changes.
   *
   * Reads offsetHeight once per rendered page, which is a forced layout — negligible at that rate,
   * and it is the only way to learn the real height.
   */
  private reportRenderedRowHeight(): void {
    const host = this.elementRef.nativeElement as HTMLElement;
    const rendered = this.findRenderedRow(host);

    if (!rendered) return;

    // Skeleton rows stand in for content that has not arrived, and their fixed placeholder text can
    // sit at a different height than the real values (which wrap, or carry badges and avatars). Only
    // a row showing real content describes the layout we are sizing for.
    if (rendered.row.querySelector(PaginationComponent.SKELETON_MARKER_SELECTOR)) return;

    PaginationUtilities.reportRowHeight(
      PaginationUtilities.getListKey(host),
      rendered.layout,
      rendered.row.offsetHeight
    );
  }

  /**
   * Locates one rendered item to measure, whichever layout the current breakpoint is showing, and
   * reports which layout that was — the two are decided together so they cannot disagree.
   */
  private findRenderedRow(host: HTMLElement): { row: HTMLElement, layout: ListLayouts } | null {
    // A table paginates from its own <tfoot>, so its rows are right there.
    const tableRow = host.closest(PaginationComponent.TABLE_SELECTOR)
      ?.querySelector(PaginationComponent.TABLE_ROW_SELECTOR) as HTMLElement | null;

    if (tableRow) return { row: tableRow, layout: ListLayouts.Table };

    // Card layouts paginate from a div placed after the cards, but not always at the same depth:
    // lists sit their pagination among the cards, so the preceding sibling is a single card, while
    // audit logs sits one pagination after both of its stacks, so the preceding sibling holds every
    // card. Step over the stacks this breakpoint has hidden (they have no height), then step inside
    // a stack — recognisable by holding exactly one element per item — to reach a single card.
    let sibling = host.previousElementSibling as HTMLElement | null;

    while (sibling && !sibling.offsetHeight) {
      sibling = sibling.previousElementSibling as HTMLElement | null;
    }

    const isStackOfItems = this.itemCount() > 1 && sibling?.childElementCount === this.itemCount();
    const card = (isStackOfItems ? sibling!.firstElementChild : sibling) as HTMLElement | null;

    return card ? { row: card, layout: ListLayouts.Cards } : null;
  }

  private startScrollTracking(): void {
    this.removeScrollListener();

    this.scrollContainer = this.findScrollContainer();
    this.checkScrollPosition();

    this.scrollHandler = () => {
      if (this.rafId !== null) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.checkScrollPosition();
      });
    };

    if (this.scrollContainer) {
      this.scrollContainer.addEventListener('scroll', this.scrollHandler, { passive: true });
      this.isListening = true;
    }
  }

  private checkScrollPosition(): void {
    let scrollTop = 0;
    let scrollHeight = 0;
    let viewportHeight = window.innerHeight;

    if (this.scrollContainer instanceof Element) {
      scrollTop = this.scrollContainer.scrollTop;
      scrollHeight = this.scrollContainer.scrollHeight;
      viewportHeight = this.scrollContainer.clientHeight;
    } else {
      scrollTop = window.scrollY;
      scrollHeight = document.documentElement.scrollHeight;
    }

    const shouldShow = scrollHeight > viewportHeight && scrollTop > 100;

    if (this.showJumpToTop() !== shouldShow) {
      this.showJumpToTop.set(shouldShow);
      this.cdr.markForCheck();
    }
  }

  private findScrollContainer(): Element | Window {
    let element = this.elementRef.nativeElement as HTMLElement | null;

    while (element) {
      element = element.parentElement;
      if (!element) break;

      const style = getComputedStyle(element);
      const overflowY = style.overflowY;

      if (overflowY === 'auto' || overflowY === 'scroll') {
        return element;
      }
    }

    return window;
  }

  private removeScrollListener(): void {
    if (this.scrollHandler && this.isListening && this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
      this.isListening = false;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private stopScrollTracking(): void {
    this.removeScrollListener();
    this.scrollContainer = null;
    this.showJumpToTop.set(false);
  }

  //#endregion
}
